import { useQuery } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronsUpDown,
  ChevronUp,
  Mail,
  MapPin,
  Phone,
  Plus,
  Search,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import cloudflare from "@/integrations/cloudflare/client";
import { devLog } from "@/lib/env";

interface Customer {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface CustomerListProps {
  onAdd: () => void;
  onEdit: (customer: Customer) => void;
  onView: (customer: Customer) => void;
  refreshTrigger?: number;
}

type SortField = "name" | "email" | "city" | "created_at";
type SortOrder = "asc" | "desc";

const SortIcon = ({
  activeField,
  field,
  sortOrder,
}: {
  activeField: SortField;
  field: SortField;
  sortOrder: SortOrder;
}) => {
  if (activeField !== field) {
    return <ChevronsUpDown className="ml-2 h-4 w-4 text-gray-400" />;
  }
  return sortOrder === "asc" ? (
    <ChevronUp className="ml-2 h-4 w-4" />
  ) : (
    <ChevronDown className="ml-2 h-4 w-4" />
  );
};

export function CustomerList({ onAdd, onEdit, onView, refreshTrigger }: CustomerListProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState<SortField>("created_at");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [filterCity, setFilterCity] = useState<string>("all");

  // Fetch customers
  const {
    data: customersData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["customers", refreshTrigger],
    queryFn: async () => {
      devLog("Fetching customers from:", `${cloudflare.apiUrl}/api/v1/customers?limit=100`);
      const response = await fetch(`${cloudflare.apiUrl}/api/v1/customers?limit=100`);

      if (!response.ok) {
        throw new Error(`Failed to fetch customers: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      devLog("API Response:", result);

      if (!result.data) {
        return [];
      }

      return result.data as Customer[];
    },
  });

  // Get unique cities for filter
  const cities = useMemo(() => {
    if (!customersData) {
      return [];
    }
    const uniqueCities = [...new Set(customersData.map((c) => c.city).filter(Boolean))];
    return uniqueCities.sort();
  }, [customersData]);

  // Filter and sort customers
  const filteredAndSortedCustomers = useMemo(() => {
    if (!customersData) {
      return [];
    }

    let filtered = [...customersData];

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase().trim();
      filtered = filtered.filter((customer) => {
        // Create full name for searching
        const fullName = `${customer.first_name || ""} ${customer.last_name || ""}`.toLowerCase();
        const reverseFullName =
          `${customer.last_name || ""} ${customer.first_name || ""}`.toLowerCase();

        // Create a searchable string with all customer fields
        const searchableFields = [
          customer.first_name,
          customer.last_name,
          fullName,
          customer.email,
          customer.phone || "",
          customer.address || "",
          customer.city || "",
          customer.state || "",
          customer.zip_code || "",
          customer.country || "",
          customer.notes || "",
        ]
          .join(" ")
          .toLowerCase();

        // Check if search term matches full name, reverse name, or any field
        return (
          fullName.includes(search) ||
          reverseFullName.includes(search) ||
          searchableFields.includes(search) ||
          // Also check individual words from search term
          search
            .split(" ")
            .every((word) => searchableFields.includes(word.toLowerCase()))
        );
      });
    }

    // Apply city filter
    if (filterCity !== "all") {
      filtered = filtered.filter((customer) => customer.city === filterCity);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: string | number = "";
      let bValue: string | number = "";

      switch (sortField) {
        case "name":
          aValue = `${a.first_name || ""} ${a.last_name || ""}`.toLowerCase();
          bValue = `${b.first_name || ""} ${b.last_name || ""}`.toLowerCase();
          break;
        case "email":
          aValue = (a.email || "").toLowerCase();
          bValue = (b.email || "").toLowerCase();
          break;
        case "city":
          aValue = a.city?.toLowerCase() || "";
          bValue = b.city?.toLowerCase() || "";
          break;
        case "created_at":
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
        default:
          return 0;
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      }
      return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
    });

    return filtered;
  }, [customersData, searchTerm, filterCity, sortField, sortOrder]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">Loading customers...</div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-red-600">
            Error loading customers: {error instanceof Error ? error.message : "Unknown error"}
            <div className="text-sm mt-2 text-gray-600">
              Please check if the API server is running on {cloudflare.apiUrl}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>Customers</CardTitle>
          <Button onClick={onAdd}>
            <Plus className="mr-2 h-4 w-4" />
            Add Customer
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, phone, address, city..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
          <Select value={filterCity} onValueChange={setFilterCity}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Filter by city" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Cities</SelectItem>
              {cities.map((city) => (
                <SelectItem key={city} value={city}>
                  {city}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Results count */}
        <div className="mb-4 text-sm text-muted-foreground">
          Showing {filteredAndSortedCustomers.length} of {customersData?.length || 0} customers
        </div>

        {/* Table */}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => toggleSort("name")}
                >
                  <div className="flex items-center">
                    Name
                    <SortIcon activeField={sortField} field="name" sortOrder={sortOrder} />
                  </div>
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => toggleSort("email")}
                >
                  <div className="flex items-center">
                    Email
                    <SortIcon activeField={sortField} field="email" sortOrder={sortOrder} />
                  </div>
                </TableHead>
                <TableHead>Phone</TableHead>
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => toggleSort("city")}
                >
                  <div className="flex items-center">
                    Location
                    <SortIcon activeField={sortField} field="city" sortOrder={sortOrder} />
                  </div>
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => toggleSort("created_at")}
                >
                  <div className="flex items-center">
                    Created
                    <SortIcon activeField={sortField} field="created_at" sortOrder={sortOrder} />
                  </div>
                </TableHead>
                <TableHead className="w-[140px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAndSortedCustomers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    {searchTerm || filterCity !== "all"
                      ? "No customers found matching your filters"
                      : "No customers yet. Add your first customer to get started."}
                  </TableCell>
                </TableRow>
              ) : (
                filteredAndSortedCustomers.map((customer) => (
                  <TableRow key={customer.id} className="hover:bg-muted/50">
                    <TableCell className="font-medium">
                      <button
                        type="button"
                        onClick={() => onView(customer)}
                        className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left"
                      >
                        {customer.first_name} {customer.last_name}
                      </button>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Mail className="h-3 w-3 text-muted-foreground" />
                        <span className="text-blue-600">{customer.email}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {customer.phone && (
                        <div className="flex items-center gap-1">
                          <Phone className="h-3 w-3 text-muted-foreground" />
                          <span className="text-blue-600">{customer.phone}</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {customer.city && (
                        <div className="flex items-center gap-1">
                          <MapPin className="h-3 w-3 text-muted-foreground" />
                          <span className="text-sm">
                            {customer.city}
                            {customer.state && `, ${customer.state}`}
                          </span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {new Date(customer.created_at).toLocaleDateString()}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => onView(customer)}
                        >
                          View
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => onEdit(customer)}
                        >
                          Edit
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
